import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { getWithParams, downloadFile } from "../../utils/fetchAPI";
import { FaSearch, FaFileExcel } from "react-icons/fa";
import { MdClear } from "react-icons/md";
import PageTitle from "../../components/page-title/pageTitle";
import { MdAssessment } from "react-icons/md";
import CustomPagination from "../../components/pagination/CustomPagination";
import Loader from "../../components/loader/loder";
import { toast } from "react-toastify";

const MostPrescribedMedicines = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");
    const [exporting, setExporting] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setError("");
        try {
            const params = {
                page: currentPage,
                limit: perPage,
            };
            
            // Only add non-empty parameters
            if (search.trim()) params.search = search.trim();
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const response = await getWithParams("/dashboard/most-prescribed-medicines", params);

            if (response.data) {
                setData(response.data.medicines || []);
                setTotalRows(response.data.total || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Failed to fetch data. Please try again.");
            setData([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [currentPage, perPage]);

    const handleSearch = () => {
        setCurrentPage(1);
        fetchData();
    };

    const handleClearFilters = () => {
        setSearch("");
        setStartDate("");
        setEndDate("");
        setCurrentPage(1);
        // Trigger fetch after clearing
        setTimeout(() => {
            fetchData();
        }, 0);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleExport = async () => {
        // Validate date range
        if (!startDate || !endDate) {
            toast.info("Please select date range (Start Date and End Date)");
            return;
        }

        setExporting(true);
        try {
            const params = {};
            
            // Only add non-empty parameters (same filters as fetchData)
            if (search.trim()) params.search = search.trim();
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            // Generate filename with current date
            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `most-prescribed-medicines-${dateStr}.xlsx`;

            await downloadFile("/dashboard/export-most-prescribed-medicines", params, filename);
            toast.success("Excel file downloaded successfully!");
        } catch (error) {
            console.error("Error exporting data:", error);
            toast.error("Failed to export data. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const columns = [
        {
            name: '#',
            selector: (row, index) => (currentPage - 1) * perPage + index + 1,
            sortable: true,
            width: '60px',
            center: true,
            compact: true,
        },
        {
            name: "Medicine Name",
            selector: (row) => row.name || 'N/A',
            sortable: true,
            wrap: true,
            grow: 1,
            minWidth: '120px',
        },
        // {
        //     name: "Generic",
        //     selector: (row) => row.generic || 'N/A',
        //     sortable: true,
        //     wrap: true,
        //     minWidth: '150px',
        //     grow: 1,
        //     omit: false,
        // },
        {
            name: "Type",
            selector: (row) => row.type || 'N/A',
            sortable: true,
            wrap: true,
            grow: 1,
            minWidth: '100px',
            hide: 'sm',
        },
        {
            name: "Manufacturer",
            selector: (row) => row.manufacturer || 'N/A',
            sortable: true,
            wrap: true,
            grow: 1,
            minWidth: '120px',
            hide: 'md',
        },
       
        // {
        //     name: "Power",
        //     selector: (row) => row.power || 'N/A',
        //     sortable: true,
        //     wrap: true,
        //     grow: 1,
        //     minWidth: '100px',
        //     hide: 'sm',
        // },
        {
            name: "Count",
            selector: (row) => row.count || 0,
            sortable: true,
            grow: 1,
            minWidth: '100px',
            center: true,
            cell: (row) => (
                <div className="font-semibold text-primary">
                    {row.count || 0}
                </div>
            ),
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#50b6b5',
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: 'bold',
                paddingLeft: '12px',
                paddingRight: '12px',
                textAlign: 'left',
            },
        },
        rows: {
            style: {
                fontSize: '0.875rem',
                minHeight: '56px',
            },
            stripedStyle: {
                backgroundColor: '#f6f6f6',
            },
            highlightOnHoverStyle: {
                backgroundColor: '#e0e0e0',
                cursor: 'pointer',
            },
        },
        cells: {
            style: {
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        table: {
            style: {
                tableLayout: 'auto',
            },
        },
        tableWrapper: {
            style: {
                display: 'table',
                width: '100%',
            },
        },
    };

    return (
        <div className='flex flex-col w-full gap-5 px-2 sm:px-0'>
            {/* Page Title */}
            <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
                <PageTitle icon={<MdAssessment />} title="Most Prescribed Medicines" />
                
                {/* Search Bar and Export Button - Full width on mobile */}
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <div className="flex items-center border-2 rounded-md w-full sm:w-80">
                        <span className="flex items-center justify-center rounded-bl rounded-tl px-3 text-zinc-500  h-full">
                            <FaSearch className="text-black" />
                        </span>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Search by medicine name"
                            className="text-sm px-3 py-2 text-black rounded-md w-full placeholder-gray-500 focus:outline-teal-800"
                        />
                    </div>
                    <button
                        onClick={handleExport}
                        disabled={exporting}
                        className="text-sm px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors duration-200 font-medium shadow-sm hover:shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap">
                        <FaFileExcel className="text-base" />
                        {exporting ? "Exporting..." : "Export Excel"}
                    </button>
                </div>
            </div>

            {/* Date Filters - Responsive Grid */}
            <div className="bg-white rounded-md p-4 drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800"
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-gray-700">End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="text-sm px-3 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800"
                        />
                    </div>
                    <button
                        onClick={handleSearch}
                        className="text-sm px-6 py-2 bg-primary text-white rounded-md hover:bg-teal-700 transition-colors duration-200 font-medium shadow-sm hover:shadow-md">
                        <span className="flex items-center justify-center gap-2">
                            <FaSearch className="text-sm" />
                            Apply Filters
                        </span>
                    </button>
                    <button
                        onClick={handleClearFilters}
                        className="text-sm px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors duration-200 font-medium shadow-sm hover:shadow-md">
                        <span className="flex items-center justify-center gap-2">
                            <MdClear className="text-lg" />
                            Clear Filters
                        </span>
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Table Section */}
            {loading ? (
                <div className="flex justify-center items-center py-10">
                    <Loader />
                </div>
            ) : (
                <div className="bg-white rounded-md drop-shadow-[0_0px_20px_rgba(203,229,255,0.3)] overflow-hidden">
                    {data.length > 0 ? (
                        <div className="overflow-x-auto">
                            <DataTable
                                columns={columns}
                                data={data}
                                customStyles={customStyles}
                                highlightOnHover
                                striped
                                responsive
                                dense={false}
                                fixedHeader
                                fixedHeaderScrollHeight="600px"
                                noDataComponent={
                                    <div className="text-center py-10 text-gray-500">
                                        No medicines found matching your criteria
                                    </div>
                                }
                            />
                        </div>
                    ) : (
                        <div className="text-center py-20 text-gray-500">
                            <div className="flex flex-col items-center gap-2">
                                <MdAssessment className="text-6xl text-gray-300" />
                                <p className="text-lg font-medium">No data available</p>
                                <p className="text-sm">Try adjusting your filters or search criteria</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Custom Pagination */}
            {!loading && data.length > 0 && (
                <CustomPagination
                    currentPage={currentPage}
                    totalRows={totalRows}
                    perPage={perPage}
                    setCurrentPage={setCurrentPage}
                    setPerPage={setPerPage}
                />
            )}
        </div>
    );
};

export default MostPrescribedMedicines;
